import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { FilterInput } from '../filter-input'
import { FilterDate } from '../filter-date'
import { FilterSwitch } from '../filter-switch'
import { FilterAutocomplete } from '../filter-autocomplete'
import { FilterBasicInput } from '../filter-basic-input'

/**
 * @typedef {object} FilterURLProps
 * @property {'input' | 'switch' | 'date' | 'autocomplete' | 'basic-input'} [filterType='input'] - Tipo de filtro a renderizar.
 * @property {React.ReactNode} [filterComponent] - Componente personalizado de filtro (anula filterType).
 * @property {string} name - Nombre del parámetro de filtro en la URL.
 * @property {string} [label] - Etiqueta del filtro.
 * @property {Array<string>} [cleanup] - Parámetros de URL a eliminar al cambiar este filtro.
 * @property {boolean} [cleanSelf] - Si es true (y switch), elimina el parámetro al desactivar el switch.
 * @property {any} [default] - Valor por defecto del filtro.
 * @property {...any} [item] - Demás props específicas del tipo de filtro.
 */

/**
 * Componente wrapper para filtros gestionados automáticamente mediante URL.
 *
 * Gestiona automáticamente la sincronización entre filtros y parámetros de URL (query string),
 * persistencia en localStorage, paginación automática, y limpieza de filtros relacionados.
 * Soporta distintos tipos de filtros: input, switch, date, autocomplete, basic-input, o un
 * componente personalizado.
 *
 * @component
 * @example
 * <FilterURL
 *   filterType="input"
 *   name="status"
 *   label="Estado"
 *   values={[{ key: 'active', value: 'Activo' }]}
 * />
 *
 * <FilterURL
 *   filterType="switch"
 *   name="showInactive"
 *   label="Mostrar inactivos"
 *   cleanSelf={true}
 * />
 *
 * @param {FilterURLProps} props - Las props del componente FilterURL.
 */
export const FilterURL = ({ filterType = 'input', filterComponent, ...item }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const appName = location.pathname.substring(location.pathname.lastIndexOf('/') + 1)
  const currentSearch = new URLSearchParams(location.search)

  useEffect(() => {
    const value = localStorage.getItem(appName + item.name)
    if (value) {
      currentSearch.set(item.name, value)
    }
  }, [])

  //Url managing functions (added conveniently to not yet assembled filters)
  const handleChangeFilter = (event, cleanup) => {
    currentSearch.set('_pagina', 1)
    currentSearch.set(
      event.target.name,
      Array.isArray(event.target.value) ? event.target.value.join(',') : event.target.value
    )
    localStorage.setItem(appName + event.target.name, event.target.value)
    if (cleanup) {
      cleanup.forEach((field) => {
        currentSearch.delete(field)
        localStorage.removeItem(appName + field)
      })
    }
    navigate({ search: '?' + currentSearch.toString() })
  }
  const handleToggleFilter = (event, cleanup, cleanSelf) => {
    currentSearch.set('_pagina', 1)
    const value = !(currentSearch.get(event.target.name) === 'true')
    currentSearch.set(event.target.name, value)
    localStorage.setItem(appName + event.target.name, value)
    if (cleanup) {
      cleanup.forEach((field) => {
        currentSearch.delete(field)
        localStorage.removeItem(appName + field)
      })
    }
    if (cleanSelf && !value) {
      currentSearch.delete(event.target.name)
      localStorage.removeItem(appName + event.target.name)
    }
    navigate({ search: '?' + currentSearch.toString() })
  }

  //Filter choice (applied unless component is submitted already assembled)
  const defaultComponent = (type, { cleanSelf, cleanup, ...item }) => {
    if (!item.name) {
      throw new Error('A nameless filter cannot be managed automatically via url')
    }
    switch (type) {
      case 'switch':
        return (
          <FilterSwitch
            {...{
              onClick: (event) => handleToggleFilter(event, cleanup, cleanSelf),
              checked: currentSearch.get(item.name) === 'true',
              ...item,
            }}
          />
        )
      case 'date':
        const { minDateField, maxDateField, ...newItem } = item
        return (
          <FilterDate
            {...{
              onChange: (event) => handleChangeFilter(event, cleanup),
              value: currentSearch.get(item.name) || item.default,
              minDate: currentSearch.get(minDateField),
              maxDate: currentSearch.get(maxDateField),
              ...newItem,
            }}
          />
        )
      case 'autocomplete':
        return (
          <FilterAutocomplete
            {...{
              onChange: (event) => handleChangeFilter(event, item.cleanup),
              value: currentSearch.get(item.name) || item.default,
              ...item,
            }}
          />
        )
      case 'basic-input':
        return (
          <FilterBasicInput
            {...{
              onChange: (event) => handleChangeFilter(event, item.cleanup),
              value: currentSearch.get(item.name) || item.default,
              ...item,
            }}
          />
        )
      case 'input':
      default:
        return (
          <FilterInput
            {...{
              onChange: (event) => handleChangeFilter(event, item.cleanup),
              value: currentSearch.get(item.name) || item.default,
              ...item,
            }}
          />
        )
    }
  }
  return filterComponent || defaultComponent(filterType, item)
}

export default FilterURL
